import type { CSSProperties } from "react";
import { Space, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { isMainShippingCostProvided } from "../../utils/leancustomsOrderCosts";

export type LeanCustomsReadinessState = {
  invoiceOk: boolean;
  docOk: boolean;
  shippingOk: boolean;
  allOk: boolean;
};

export const READINESS_ATTENTION_STYLE: CSSProperties = {
  background: "#fff7e6",
  border: "1px solid #ffa940",
  borderRadius: 6,
};

export const READINESS_ATTENTION_TAG_STYLE: CSSProperties = {
  margin: 0,
  color: "#d46b08",
  background: "#fff7e6",
  borderColor: "#ffa940",
};

export function getLeanCustomsReadinessState(args: {
  formValues: Record<string, unknown>;
  hasCommercialInvoice: boolean;
  hasTariff: boolean;
  manualShipping: number;
  estimatedTransportLoading: boolean;
  requiresShippingEstimate?: boolean;
}): LeanCustomsReadinessState {
  const doc = String(args.formValues.document_number ?? "").trim();
  const docOk = doc.length > 0;
  const overrideShip = args.formValues.estimated_shipping_override as
    | number
    | undefined
    | null;

  const shippingOk = isMainShippingCostProvided({
    requiresShippingEstimate: args.requiresShippingEstimate ?? true,
    hasTariff: args.hasTariff,
    manualShipping: args.manualShipping,
    estimatedShippingOverride: overrideShip,
    estimatedTransportLoading: args.estimatedTransportLoading,
  });

  const invoiceOk = args.hasCommercialInvoice;

  return {
    invoiceOk,
    docOk,
    shippingOk,
    allOk: invoiceOk && docOk && shippingOk,
  };
}

function ReadinessTag({
  ok,
  label,
}: {
  ok: boolean;
  label: string;
}) {
  return (
    <Tag
      icon={ok ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
      color={ok ? "success" : undefined}
      style={ok ? { margin: 0 } : READINESS_ATTENTION_TAG_STYLE}
    >
      {label}
    </Tag>
  );
}

export function LeanCustomsStep1ReadinessCard({
  formValues,
  hasCommercialInvoice,
  hasTariff,
  manualShipping,
  estimatedTransportLoading,
  isAir = false,
  requiresShippingEstimate = true,
}: {
  formValues: Record<string, unknown>;
  hasCommercialInvoice: boolean;
  hasTariff: boolean;
  manualShipping: number;
  estimatedTransportLoading: boolean;
  isAir?: boolean;
  requiresShippingEstimate?: boolean;
}) {
  const readiness = getLeanCustomsReadinessState({
    formValues,
    hasCommercialInvoice,
    hasTariff,
    manualShipping,
    estimatedTransportLoading,
    requiresShippingEstimate,
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 16,
        padding: "8px 12px",
        background: readiness.allOk ? "#f6ffed" : "#fff7e6",
        border: `1px solid ${readiness.allOk ? "#b7eb8f" : "#ffa940"}`,
        borderRadius: 6,
      }}
    >
      <Typography.Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
        Ready:
      </Typography.Text>
      <Space size={[6, 6]} wrap>
        <ReadinessTag ok={readiness.invoiceOk} label="Invoice" />
        <ReadinessTag ok={readiness.docOk} label="AWB / B/L" />
        <ReadinessTag
          ok={readiness.shippingOk}
          label={isAir ? "Air shipping cost" : "Shipping cost"}
        />
      </Space>
    </div>
  );
}

/** Disable “Create order” until required booking data is present. */
export function isLeanCustomsStep1ReadyForCreate(
  formValues: Record<string, unknown>,
  hasCommercialInvoice = false,
  options?: {
    requiresShippingEstimate?: boolean;
    hasTariff?: boolean;
    manualShipping?: number;
    estimatedTransportLoading?: boolean;
  }
): boolean {
  return getLeanCustomsReadinessState({
    formValues,
    hasCommercialInvoice,
    hasTariff: options?.hasTariff ?? false,
    manualShipping: options?.manualShipping ?? 0,
    estimatedTransportLoading: options?.estimatedTransportLoading ?? false,
    requiresShippingEstimate: options?.requiresShippingEstimate ?? true,
  }).allOk;
}
